import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FormGroupDirective, FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { NgImageSliderModule } from "ng-image-slider";
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { CookieModule } from 'ngx-cookie';
import { NgxMaskModule } from 'ngx-mask-2';
import { authInterceptorProviders } from './_helpers/auth.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DecimaLimitDirective } from './directives/decima-limit.directive';
import { AuthGuard } from './guards/auth.guard';
import { HeaderEmvsComponent } from './layout/header-emvs/header-emvs.component';
import { LoginComponent } from './login/login.component';
import { ModalComponent } from './modal/modal.component';
import { ProfileComponent } from './profile/profile.component';

import { NgPipesModule } from 'ngx-pipes';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

import { FormAvisosComponent } from './avisos/form-avisos/form-avisos.component';
import { EditAvisoDialog, ListadoAvisosComponent } from './avisos/listado-avisos/listado-avisos.component';
import { ModalAvisosComponent } from './avisos/modal-avisos/modal-avisos.component';
import { MaterialModule } from './material/material.module';

import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UrlService } from './_services/urlService';
import { InfoLegalComponent } from './info-legal/info-legal/info-legal.component';
import { SliderEmvsComponent } from './layout/slider-emvs/slider-emvs.component';
import { ConfirmUpdateDialogComponent } from './modal/confirmar-update/confirm-update-dialog/confirm-update-dialog.component';
import { NotificationComponent } from './notification/notification.component';
import { CardDireccionesComponent } from './profile/card-direcciones/card-direcciones.component';


import { DragDropModule } from '@angular/cdk/drag-drop';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { RaizComponent } from './raiz/raiz.component';

import { EstadoFirmaAutorizadosDialogComponent } from './listado/listado-tramites-emvs/dialogs/estado-firma-autorizados-dialog.component';
import { ListadoTramitesEmvsComponent } from './listado/listado-tramites-emvs/listado-tramites-emvs.component';

import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ClaveComponent } from './clave/clave.component';
import { ConfirmarCorreoComponent } from './confirmar-correo/confirmar-correo.component';
import { ReloadDirectiveDirective } from './directives/reload-directive.directive';
import { EnvironmentGuard } from './guards/environment.guard';
import { NoLoginGuard } from './guards/noLogin.guard';
import { FormProcedimientoComponent } from './listado/listado-procedimientos/form-procedimiento/form-procedimiento.component';
import { EditProcedimientosDialog, ListadoProcedimientosComponent } from './listado/listado-procedimientos/listado-procedimientos.component';
import { ConfirmOtpDialogComponent } from './modal/confirm-otp/confirm-otp-dialog.component';

import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { NgxSpinnerModule } from "ngx-spinner";
import { PowerBIEmbedModule } from 'powerbi-client-angular';
import { BoardAdminComponent } from './board-admin/board-admin.component';
import { ClientLoaderService } from './core/util/client-loader.service';
import { CustomMatPaginatorIntl } from './core/util/custom-mat-paginator-intl.service';
import { LanguageService } from './core/util/language.service';
import { CustomClientTranslateLoader } from './core/util/translate-client-loader.service';
import { CuadroDeMandosComponent } from './cuadro-de-mandos/cuadro-de-mandos/cuadro-de-mandos.component';
import { GestionListadosComponent } from './gestion-listados/gestion-listados.component';
import { GestionPlantillasComponent } from './gestion-plantillas/gestion-plantillas.component';
import { RemesaDetailDrawerComponent } from './gestion-remesas/drawers/remesa-detail-drawer.component';
import { RemesaFormDrawerComponent } from './gestion-remesas/drawers/remesa-form-drawer.component';
import { GestionRemesasComponent } from './gestion-remesas/gestion-remesas.component';
import { ConfirmDeleteDialogComponent } from './gestion-usuarios/dialogs/confirm-delete-dialog.component';
import { RolesDialogComponent } from './gestion-usuarios/dialogs/roles-dialog.component';
import { UsuarioFormDialogComponent } from './gestion-usuarios/dialogs/usuario-form-dialog.component';
import { GestionUsuariosComponent } from './gestion-usuarios/gestion-usuarios.component';
import { FormBuilderTramiteComponent } from './listado/form-builder-tramite/form-builder-tramite.component';
import { ListadoErroresEmvsComponent } from './listado/listado-errores-emvs/listado-errores-emvs.component';
import { ListadoNotificacionesEmvsComponent } from './listado/listado-notificaciones-emvs/listado-notificaciones-emvs.component';
import { ListadoProcedimientosDinamicosComponent } from './listado/listado-procedimientos-dinamicos/listado-procedimientos-dinamicos.component';

export function createTranslateLoader(http: HttpClient, clientLoader: ClientLoaderService) {
  return new CustomClientTranslateLoader(http, clientLoader);
}

export function initApp(clientLoader: ClientLoaderService, languageService: LanguageService) {
  return () => clientLoader.loadClient().then(() => languageService.init());
}

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeEn, 'en-GB');

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ProfileComponent,
    BoardAdminComponent,
    ModalComponent,
    DecimaLimitDirective,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ListadoAvisosComponent,
    FormAvisosComponent,
    ModalAvisosComponent,
    EditAvisoDialog,
    EditAvisoDialog,
    ConfirmUpdateDialogComponent,
    InfoLegalComponent,
    CardDireccionesComponent,
    NotificationComponent,
    HeaderEmvsComponent,
    SliderEmvsComponent,
    RaizComponent,
    ClaveComponent,
    ReloadDirectiveDirective,
    ListadoTramitesEmvsComponent,
    EstadoFirmaAutorizadosDialogComponent,
    ListadoProcedimientosComponent,
    EditProcedimientosDialog,
    FormProcedimientoComponent,
    ListadoProcedimientosDinamicosComponent,
    FormBuilderTramiteComponent,
    ConfirmarCorreoComponent,
    ConfirmOtpDialogComponent,
    CuadroDeMandosComponent,
    ListadoNotificacionesEmvsComponent,
    ListadoErroresEmvsComponent,
    GestionUsuariosComponent,
    UsuarioFormDialogComponent,
    RolesDialogComponent,
    ConfirmDeleteDialogComponent,
    GestionListadosComponent,
    GestionRemesasComponent,
    GestionPlantillasComponent,
    RemesaFormDrawerComponent,
    RemesaDetailDrawerComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    NgImageSliderModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    TabsModule.forRoot(),
    CookieModule.withOptions(),
    NgxMaskModule.forRoot({ dropSpecialCharacters: false }),
    SimpleNotificationsModule.forRoot(),
    NgPipesModule,
    MaterialModule,
    ModalModule,
    PopoverModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    DragDropModule,
    PowerBIEmbedModule,
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, ClientLoaderService],
      },
    }),
  ],
  providers: [authInterceptorProviders,
    // errorInterceptorProviders,
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    DatePipe,
    DecimalPipe,
    AuthGuard,
    EnvironmentGuard,
    NoLoginGuard,
    MatTableDataSource,
    FormGroupDirective,
    MatDatepickerModule,
    provideAnimations(),
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [ClientLoaderService, LanguageService],
      multi: true,
    },
  ],
  exports: [
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private urlService: UrlService) { }

}